import { useState, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, CircularProgress } from '@mui/material';
import { AppContext, SttDialog, SttForm } from '../../sporttia/all';
import { getErrorMessage } from '../../lib/utils';
import type { DeviceSpace } from '../../types/models/DeviceSpace';
import useDevicesService from '../../services/DevicesService';
import { SttItemList } from '../../sporttia/lists/SttItemList';
import useScsService from '../../services/ScsService';
import { SPORTCENTER_DEVICES_QUERY_KEY } from '../../lib/queryKeys';
import { PERMISSIONS } from '../../lib/permissions';

type DeviceSpaceDialogProps = {
    item: DeviceSpace;
    onSave: () => void;
    onClose: () => void;
};

export default function DeviceSpaceDialog({
    item,
    onSave,
    onClose,
}: DeviceSpaceDialogProps) {
    const cxt = useContext(AppContext)!;
    const devicesService = useDevicesService();
    const scsService = useScsService();
    const [deviceSpace, setDeviceSpace] = useState<DeviceSpace>(item);
    const { t } = useTranslation();
    const updateDeviceSpaceMutation = devicesService.useUpdateDeviceSpace({
        onSuccess: (response: { deviceSpace: DeviceSpace }) => {
            setDeviceSpace(response.deviceSpace);
            onSave();
        },
        onError: (error: any) => {
            cxt.showMessage('E', getErrorMessage(error));
        },
    });
    const deleteDeviceSpaceMutation = devicesService.useDeleteDeviceSpace({
        onSuccess: () => {
            onSave();
            onClose();
        },
        onError: (error: any) => {
            cxt.showMessage('E', getErrorMessage(error));
        },
    });
    const createDeviceSpaceMutation = devicesService.useCreateDeviceSpace({
        onSuccess: (response: { deviceSpace: DeviceSpace }) => {
            setDeviceSpace(response?.deviceSpace);
            onSave();
        },
        onError: (error: any) => {
            cxt.showMessage('E', getErrorMessage(error));
        },
    });

    const createDeviceSpaceDeviceMutation =
        devicesService.useCreateDeviceSpaceDevice({
            onSuccess: (response: { deviceSpace: DeviceSpace }) => {
                setDeviceSpace(response.deviceSpace);
                onSave();
            },
            onError: (error: any) => {
                cxt.showMessage('E', getErrorMessage(error));
            },
        });
    const deleteDeviceSpaceDeviceMutation =
        devicesService.useDeleteDeviceSpaceDevice({
            onSuccess: (response: { deviceSpace: DeviceSpace }) => {
                setDeviceSpace(response.deviceSpace);
                onSave();
            },
            onError: (error: any) => {
                cxt.showMessage('E', getErrorMessage(error));
            },
        });
    if (
        createDeviceSpaceMutation.isPending ||
        updateDeviceSpaceMutation.isPending
    ) {
        return (
            <SttDialog
                maxWidth="md"
                open
                content={
                    <Box display="flex" justifyContent="center" paddingY={15}>
                        <CircularProgress />
                    </Box>
                }
                title={undefined}
                onClose={undefined}
                buttons={undefined}
            />
        );
    }

    const handleSave = () => {
        if (item?.id === -1) {
            createDeviceSpaceMutation.mutate({
                sportcenterId: cxt?.sc?.id ? cxt.sc?.id : -1,
                params: {
                    maximumCapacity: deviceSpace.maximumCapacity,
                    name: deviceSpace.name,
                },
            });
        } else {
            updateDeviceSpaceMutation.mutate({
                deviceSpaceId: item?.id,
                params: {
                    currentCapacity: deviceSpace.currentCapacity,
                    maximumCapacity: deviceSpace.maximumCapacity,
                },
            });
        }
    };

    const handleDelete = () => {
        deleteDeviceSpaceMutation.mutate({
            deviceSpaceId: deviceSpace?.id,
        });
    };

    return (
        <SttDialog
            open={!!item}
            title={t('deviceSpace.dialogTitle')}
            onClose={onClose}
            maxWidth="md"
            content={
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', md: 'row' },
                        gap: 2,
                    }}
                >
                    <Box sx={{ flex: 1 }}>
                        <SttForm
                            onChangeForm={({ name, value }: any) =>
                                setDeviceSpace({
                                    ...deviceSpace,
                                    [name]: value,
                                })
                            }
                            elements={[
                                {
                                    xs: 12,
                                    caption: t('generic.name'),
                                    name: 'name',
                                    show: deviceSpace?.id === -1,
                                    value: deviceSpace?.name,
                                },
                                {
                                    xs: 12,
                                    caption: t('deviceSpace.maximunCapacity'),
                                    name: 'maximumCapacity',
                                    value: deviceSpace?.maximumCapacity.toString(),
                                },
                                {
                                    xs: 12,
                                    caption: t('deviceSpace.currentCapacity'),
                                    name: 'currentCapacity',
                                    show: deviceSpace?.id !== -1,
                                    value: deviceSpace?.currentCapacity.toString(),
                                },
                            ]}
                            buttons={[]}
                        />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                        {deviceSpace.id !== -1 && (
                            <SttItemList
                                title={t('generic.devices')}
                                items={deviceSpace.devices}
                                onAdd={(deviceId: number) => {
                                    if (deviceId) {
                                        createDeviceSpaceDeviceMutation.mutate({
                                            deviceSpaceId: deviceSpace?.id,
                                            deviceId,
                                        });
                                    }
                                }}
                                onDelete={(deviceId: number) => {
                                    if (deviceId) {
                                        deleteDeviceSpaceDeviceMutation.mutate({
                                            deviceSpaceId: deviceSpace?.id,
                                            deviceId,
                                        });
                                    }
                                }}
                                queryKey={SPORTCENTER_DEVICES_QUERY_KEY}
                                itemQueryCall={
                                    scsService.getScsDevices2 as (
                                        params: unknown,
                                    ) => Promise<{
                                        rows?: { id: number; name: string }[];
                                    }>
                                }
                                queryCallParameters={{
                                    id: cxt?.sc ? cxt?.sc?.id : -1,
                                    params: {},
                                }}
                            />
                        )}
                    </Box>
                </Box>
            }
            buttons={[
                {
                    type: 'save',
                    onClick: handleSave,
                    permission: PERMISSIONS.DEVICE.MODIFY,
                },
                {
                    type: 'delete',
                    show: deviceSpace?.id !== -1,
                    onClick: handleDelete,
                    permission: PERMISSIONS.DEVICE.MODIFY,
                },
            ]}
        />
    );
}
